import type { StaffImportMapping, StaffImportPreview } from "@lafayette/schemas";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { groupBox, groupLegend, inRowTarget } from "../../catalog/components/styles";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { SelectField } from "../../ui/fields";
import { RadioInput } from "../../ui/radio";
import { StatusBadge } from "../../ui/status-badge";
import { StatusMessage } from "../../ui/status-message";
import { columnLetter } from "../columns";
import { StepCard } from "../components/StepCard";
import { rawText, stepActions } from "../components/styles";
import { delimiterLabels, encodingLabels } from "../labels";

type HeaderStepProps = Readonly<{
  /** The read file of a new draft. `null` for a saved draft. */
  preview: StaffImportPreview | null;
  /** The saved mapping of a draft: the sheet and the header row are fixed for it. */
  savedMapping: StaffImportMapping | null;
  /** A new preview is being fetched after a change. */
  busy: boolean;
  /** A sentence about a failed change (from a kind or a code, never from file text). */
  error: string | null;
  onChange: (change: Readonly<{ sheetIndex: number; headerRowNumber?: number }>) => void;
  onContinue: () => void;
}>;

type RowKind = "header" | "skipped" | "blank" | "data";

const rowStatus = (
  row: StaffImportPreview["rows"][number],
  headerRowNumber: number,
): { label: string; kind: RowKind } => {
  if (row.sourceRowNumber === headerRowNumber) return { label: "Header row", kind: "header" };
  if (row.blank) return { label: "Blank: skipped", kind: "blank" };
  if (row.sourceRowNumber < headerRowNumber)
    return { label: "Above the header: skipped", kind: "skipped" };
  return { label: "Data", kind: "data" };
};

/** One row of the sheet as the table shows it. It carries what its cells need, so the column list never changes with the choice. */
type SheetRow = Readonly<{
  sourceRowNumber: number;
  cells: readonly string[];
  blank: boolean;
  status: { label: string; kind: RowKind };
  checked: boolean;
  onChoose: () => void;
}>;

const column = createColumnHelper<SheetRow>();

function sheetColumns(columnCount: number): ColumnDef<SheetRow, unknown>[] {
  return [
    column.display({
      id: "choose",
      header: "Header row",
      cell: (info) => {
        const row = info.row.original;
        return (
          // biome-ignore lint/a11y/noLabelWithoutControl: the radio is inside
          <label
            className={cn(
              "inline-flex size-11 cursor-pointer items-center justify-center compact:size-8",
              inRowTarget,
            )}
          >
            <RadioInput
              name="import-header-row"
              value={row.sourceRowNumber}
              checked={row.checked}
              disabled={row.blank}
              aria-label={`Use row ${row.sourceRowNumber} as the header row`}
              onChange={row.onChoose}
            />
          </label>
        );
      },
    }),
    column.accessor("sourceRowNumber", { id: "row", header: "Row", meta: { rowHeader: true } }),
    column.display({
      id: "use",
      header: "Use",
      cell: (info) => {
        const { status } = info.row.original;
        return (
          <span data-row-kind={status.kind} className="inline-flex">
            {status.kind === "header" ? (
              <StatusBadge tone="info">{status.label}</StatusBadge>
            ) : (
              <Badge tone="outline">{status.label}</Badge>
            )}
          </span>
        );
      },
    }),
    ...Array.from({ length: columnCount }, (_, index) =>
      column.display({
        id: `cell-${index}`,
        header: columnLetter(index),
        cell: (info) => info.row.original.cells[index] ?? "",
        meta: { className: cn("min-w-30 max-w-56", rawText) },
      }),
    ),
  ] as ColumnDef<SheetRow, unknown>[];
}

/**
 * Step 2. The sheet and the row that holds the column names. A saved draft shows them read-only: the
 * server fixes them for the job. For a new draft, a change reads the stored file again (P1).
 */
export function HeaderStep({
  preview,
  savedMapping,
  busy,
  error,
  onChange,
  onContinue,
}: HeaderStepProps) {
  const columnCount =
    preview === null
      ? 0
      : Math.max(preview.columnCount, ...preview.rows.map((row) => row.cells.length));
  const columns = useMemo(() => sheetColumns(columnCount), [columnCount]);

  if (preview === null) {
    return (
      <StepCard step="header" title="Sheet and header row">
        {savedMapping === null ? (
          <p>No file has been read yet.</p>
        ) : (
          <>
            <p>
              Sheet{" "}
              <strong>
                {savedMapping.sourceSheetName || `${savedMapping.sourceSheetIndex + 1}`}
              </strong>
              , header row <strong>{savedMapping.headerRowNumber}</strong>. They are fixed for this
              draft. To change them, cancel the draft and start again from the same file.
            </p>
            <p className="text-sm text-muted-foreground">
              {savedMapping.sourceHeaders.length} columns. Encoding:{" "}
              {encodingLabels[savedMapping.sourceEncoding]}. Delimiter:{" "}
              {delimiterLabels[savedMapping.sourceDelimiter]}.
            </p>
          </>
        )}
        <div className={stepActions}>
          <Button type="button" onClick={onContinue}>
            Continue to the mapping
          </Button>
        </div>
      </StepCard>
    );
  }

  const hasDuplicates = preview.columns.some((column) => column.notes.includes("DUPLICATE_HEADER"));
  const hasBlankHeaders = preview.columns.some(
    (column) => column.notes.includes("BLANK_HEADER") || column.header.trim() === "",
  );
  const rows: SheetRow[] = preview.rows.map((row) => ({
    sourceRowNumber: row.sourceRowNumber,
    cells: row.cells,
    blank: row.blank,
    status: rowStatus(row, preview.headerRowNumber),
    checked: row.sourceRowNumber === preview.headerRowNumber,
    onChoose: () =>
      onChange({ sheetIndex: preview.sheetIndex, headerRowNumber: row.sourceRowNumber }),
  }));

  return (
    <StepCard step="header" title="Sheet and header row" busy={busy}>
      <p>
        Choose the sheet, then the row that holds the column names.
        {preview.headerRowGuessed ? " The server guessed the header row. Check it." : ""}
      </p>

      {error === null ? null : <StatusMessage message={{ tone: "error", text: error }} />}

      {preview.sheets.length > 1 ? (
        <div className="max-w-md">
          <SelectField
            id="import-sheet"
            label="Sheet"
            value={String(preview.sheetIndex)}
            disabled={busy}
            onChange={(value) => onChange({ sheetIndex: Number(value) })}
            options={preview.sheets.map((sheet) => ({
              value: String(sheet.index),
              label: sheet.name === "" ? `Sheet ${sheet.index + 1}` : sheet.name,
            }))}
          />
        </div>
      ) : (
        <p>
          Sheet: <strong>{preview.sheets[0]?.name || "Sheet 1"}</strong>
        </p>
      )}

      <fieldset className={cn(groupBox, "min-w-0")} disabled={busy}>
        <legend className={groupLegend}>Header row</legend>
        <DataTable
          banded
          columns={columns}
          data={rows}
          getRowId={(row) => String(row.sourceRowNumber)}
          caption={`First ${preview.rows.length} rows of the sheet. Choose the row that holds the column names.`}
          captionVisible
        />
      </fieldset>

      <div role="status" aria-live="polite">
        <p>
          <strong>{preview.rowCount}</strong> data {preview.rowCount === 1 ? "row" : "rows"} under
          the header row, <strong>{preview.columnCount}</strong>{" "}
          {preview.columnCount === 1 ? "column" : "columns"}.
        </p>
      </div>
      <ul className="m-0 grid list-disc gap-1 pl-5" aria-label="What the server found">
        {preview.sourceEncoding === "NOT_APPLICABLE" ? null : (
          <li>Encoding: {encodingLabels[preview.sourceEncoding]}.</li>
        )}
        {preview.sourceDelimiter === "NOT_APPLICABLE" ? null : (
          <li>Delimiter: {delimiterLabels[preview.sourceDelimiter]}.</li>
        )}
        {preview.leadingRowsSkipped > 0 ? (
          <li>{preview.leadingRowsSkipped} rows above the header are skipped.</li>
        ) : null}
        {preview.blankRowsSkipped > 0 ? (
          <li>{preview.blankRowsSkipped} blank rows are skipped.</li>
        ) : null}
        {preview.formulaCellCount > 0 ? (
          <li>
            {preview.formulaCellCount} cells hold formulas. The saved value of each cell is used,
            not the formula.
          </li>
        ) : null}
        {hasDuplicates ? (
          <li>
            Some columns have the same header. You can tell them apart by their column letter.
          </li>
        ) : null}
        {hasBlankHeaders ? (
          <li>Some columns have no header. They stay not mapped until you decide.</li>
        ) : null}
      </ul>

      <div className={stepActions}>
        <Button type="button" onClick={onContinue} disabled={busy}>
          Continue to the mapping
        </Button>
      </div>
    </StepCard>
  );
}
